/**
 * A equipe de vendas: resultado, uso da Sophia e o que dá para fazer a respeito.
 *
 * POR QUE ESTA TELA EXISTE
 *
 * O módulo de vendas não tinha nenhuma tela sobre vendedores. Havia o KPI da
 * empresa e um dashboard — nada respondia ao que o gerente da Jotuje pergunta
 * todo dia: *quem está vendendo, quem parou, quem ainda não tem a Sophia e
 * quanto isso está custando*.
 *
 * O vendedor não entra na plataforma; ele fala com a Sophia no WhatsApp. Esta
 * é a visão que o gerente tem da equipe — e o único lugar onde liberar ou
 * revogar esse acesso não exige rodar script.
 *
 * OS DOIS NÚMEROS QUE NÃO SÃO O MESMO
 *
 * "Com Sophia" é quem TEM acesso. "Usaram" é quem FALOU com ela nos últimos
 * 15 dias. Medido na Jotuje em 24/09/2026: 115 com acesso, **zero usaram** —
 * o único telefone que conversou no período é o do admin, não da equipe. Um
 * cartão só contaria a metade boa da história.
 *
 * POR QUE A JANELA É DE 15 DIAS
 *
 * O turno expira por TTL no DynamoDB. Não é recorte escolhido: é o que existe.
 * A tela diz a janela em vez de deixar supor que é o mês.
 *
 * "SEM TOKEN" NÃO É BURACO
 *
 * Turno resolvido por código determinístico não chama o modelo e custa zero —
 * dos 68 turnos vivos medidos, 43 foram assim. O que merece alarme é outro
 * contador: turnos bloqueados por falta de chave do cliente.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, Drawer, FormControlLabel, IconButton,
  InputAdornment, MenuItem, Radio, RadioGroup, Skeleton, Snackbar, Stack, Switch, Table, TableBody,
  TableCell, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  useMediaQuery, useTheme,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import WavingHandRoundedIcon from "@mui/icons-material/WavingHandRounded";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../context/TenantContext";
import { useAccess } from "../context/AccessContext";

type Vendedor = {
  chave: string;
  nome: string;
  unidade: string | null;
  unidade_chave: string | null;
  gerente: string | null;
  telefone: string | null;
  telefone_vinculo: string | null;
  /** A Sophia atende (mesmo portão da conversa). */
  tem_whatsapp: boolean;
  /** Tem vínculo, atendido ou não. Com vínculo, liberar não pede número. */
  tem_vinculo?: boolean;
  /** template -> quando foi o último envio para este vendedor */
  envios?: Record<string, string>;
  ativo: boolean;
  ultima_venda: string | null;
  liquido_atual: number | null;
  liquido_antes: number | null;
  delta_pct: number | null;
  pedidos_atual: number;
  pedidos_antes: number;
  clientes_atual: number;
  turnos: number;
  tokens: number;
  custo_brl: number | null;
  ultimo_turno: string | null;
  bloqueios_sem_chave: number;
};

type Resumo = {
  total: number; ativos: number; venderam: number; com_whatsapp: number;
  parados: number; usaram_sophia: number; tokens: number; custo_brl: number;
  bloqueios_sem_chave: number;
};

/** Template que o executivo dispara para um vendedor (um por vez, manual). */
type Template = {
  id: string; rotulo: string; nome: string;
  /** APPROVED, PENDING, REJECTED… ou NAO_EXISTE / INDISPONIVEL */
  status: string; status_legivel: string; motivo: string; categoria: string;
  corpo: string;
};

const ICONE_DO_TEMPLATE: Record<string, typeof SendRoundedIcon> = {
  boas_vindas: WavingHandRoundedIcon,
  lembrete_diario: WbSunnyRoundedIcon,
};

/** "PEDRO HENRIQUE" -> "Pedro Henrique", como o backend faz no envio. */
const nomeDeSaudacao = (nome: string) =>
  nome.trim().split(/\s+/).map((p, i) =>
    i && ["de", "da", "do", "das", "dos", "e"].includes(p.toLowerCase())
      ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
  ).join(" ");

type Resposta = {
  vendedores: Vendedor[];
  resumo: Resumo;
  periodo?: { de: string; ate: string; comparado_de: string; comparado_ate: string };
  rotulo_unidade?: string;
  unidades?: { nome: string; vendedores: number }[];
  janela_uso_dias?: number;
  stale: boolean;
  error?: string | null;
};

type Conversa = {
  turn_id: string; quando: string; pergunta: string; resposta: string;
  respondeu: boolean; status: string; tratado_por: string; erro: string | null;
  modelo: string | null; tokens_entrada: number; tokens_saida: number;
  custo_brl: number | null; duracao_ms: number;
  ferramentas: { nome: string; alvo: Record<string, string> }[];
};

type Detalhe = {
  chave: string;
  nome?: string;
  telefone_erp?: string;
  vinculo: {
    tem_vinculo: boolean;
    /** O vínculo está habilitado. Campo da INTEGRAÇÃO: o sync das 04:30 o regrava. */
    ativo: boolean;
    /** A decisão de gente. `true` liberado, `false` revogado, `null` ninguém decidiu. */
    liberado: boolean | null;
    /** A Sophia responde de fato em algum número (mesmo portão da conversa). */
    atende?: boolean;
    /** `aberta`: quem nunca foi decidido passa. `por_liberacao`: só quem foi liberado. */
    politica?: "aberta" | "por_liberacao";
    telefone: string; personas: string[];
    decidido_por?: string; decidido_em?: string | null; motivo?: string;
    liberado_por?: string; liberado_em?: string | null; equipe?: string;
    telefones?: {
      telefone: string; liberado: boolean | null; atende?: boolean;
      /** quem é a pessoa deste número, pelo diretório */
      nome?: string;
      /** template -> último envio para ESTE número */
      envios?: Record<string, string>;
      decidido_por?: string; decidido_em?: string | null; motivo?: string;
    }[];
  };
  uso: {
    turnos?: number; turnos_com_modelo?: number; tokens_entrada?: number;
    tokens_saida?: number; custo_brl?: number | null; modelo?: string;
    falhas?: number; bloqueios_sem_chave?: number; ultimo_turno?: string | null;
  };
  conversas: Conversa[];
  janela_dias: number;
};

type Ranking = {
  janela_dias: number;
  turnos_na_janela: number;
  turnos_com_registro: number;
  ferramentas: { nome: string; vezes: number }[];
  alvos: { campo: string; valor: string; vezes: number }[];
};

type Filtro = "todos" | "parados" | "sem_whatsapp" | "usaram" | "nunca_usaram";

const brl = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const brlExato = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const num = (n: number | null | undefined) => (n ?? 0).toLocaleString("pt-BR");

const dia = (iso: string | null) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

const quando = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 16)
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Dois telefones sao o mesmo numero se tiverem os mesmos digitos: o vinculo
// grava sem `+`, o ERP grava com, e quem digita usa parenteses e traco.
const soDigitos = (v: string | null | undefined) => (v ?? "").replace(/\D+/g, "");
const mesmoNumero = (a: string | null | undefined, b: string | null | undefined) =>
  !!soDigitos(a) && soDigitos(a) === soDigitos(b);

// `granted_by` guarda o email de quem clicou — ou o id do job, quando o
// vinculo veio da carga do ERP. "jotuje_vendedores_sync_a579e3fc…" nao diz
// nada a quem le; "sincronizacao automatica" diz.
const quemLiberou = (v: string | null | undefined) => {
  const s = (v ?? "").trim();
  if (!s) return "";
  return /_sync_|^[a-z_]+_[0-9a-f]{16,}$/i.test(s) ? "sincronização automática" : s;
};

export function Vendedores() {
  const api = useApi();
  const { tenant } = useTenant();
  const { can } = useAccess();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [equipe, setEquipe] = useState("");
  const [aberto, setAberto] = useState<Vendedor | null>(null);

  const podeGerenciar = can("vendedores.gerenciar");
  const cliente = useQueryClient();
  // Chaves em andamento, por vendedor: duas linhas podem ser alternadas ao
  // mesmo tempo e cada uma trava só a si mesma.
  const [alternando, setAlternando] = useState<Set<string>>(new Set());
  const [recado, setRecado] = useState<{ texto: string; desfazer?: () => void; erro?: boolean } | null>(null);

  const q = useQuery({
    queryKey: ["vendedores", tenant],
    queryFn: () => api.call<Resposta>("/api/vendedores"),
    enabled: !!tenant,
  });

  /* Templates para mandar a UM vendedor, pelo botão na linha dele.

     Manual de propósito (decisão do Lucas, 31/10/2026): nada de disparo ao
     liberar, nada de timer. O que a Meta ainda não tem não aparece; o que ela
     tem mas não aprovou aparece apagado, dizendo por quê. */
  const tpl = useQuery({
    queryKey: ["vendedores-templates", tenant],
    queryFn: () => api.call<{ assistente: string; templates: Template[] }>("/api/vendedores/templates"),
    enabled: !!tenant && podeGerenciar,
    staleTime: 60_000,
  });
  const templates = (tpl.data?.templates ?? []).filter((t) => t.status !== "NAO_EXISTE");
  const assistente = tpl.data?.assistente || "Sophia";
  const [envio, setEnvio] = useState<{ v: Vendedor; t: Template } | null>(null);
  const [numeroEscolhido, setNumeroEscolhido] = useState("");

  /* Os números do vendedor, para escolher quando há mais de um atendido.

     Seis RCAs da Jotujé têm dois ou três números (troca que ninguém limpou,
     ou gente da UpOwl testando como vendedor). Mandar para "o primeiro"
     mandava para a pessoa errada — foi o que o primeiro teste mostrou. */
  const det = useQuery({
    queryKey: ["vendedor", tenant, envio?.v.chave],
    queryFn: () => api.call<Detalhe>(`/api/vendedores/${encodeURIComponent(envio!.v.chave)}`),
    enabled: !!envio && !!tenant,
  });
  const numerosAtendidos = (det.data?.vinculo.telefones ?? []).filter((t) => t.atende);
  const numeroDoEnvio = numerosAtendidos.length === 1 ? numerosAtendidos[0].telefone : numeroEscolhido;
  /** "558591165012 · Lucas Franca · lembrete enviado em 31/10, 10:28" */
  const rotuloDoNumero = (n: { telefone: string; nome?: string; envios?: Record<string, string> }) =>
    [n.telefone, n.nome, envio && n.envios?.[envio.t.id] ? `enviado em ${quando(n.envios[envio.t.id])}` : ""]
      .filter(Boolean).join(" · ");
  const abrirEnvio = (v: Vendedor, t: Template) => { setNumeroEscolhido(""); setEnvio({ v, t }); };

  const enviarTemplate = useMutation({
    mutationFn: ({ v, t, telefone }: { v: Vendedor; t: Template; telefone: string }) =>
      api.call<{ ok: boolean; telefone: string; nome: string; template: string }>(
        `/api/vendedores/${encodeURIComponent(v.chave)}/mensagem`,
        {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ template: t.id, telefone }),
        },
      ),
    onSuccess: (r, { v }) => {
      setEnvio(null);
      // "aceita", não "entregue": a entrega a Meta confirma depois, no webhook.
      setRecado({ texto: `"${r.template}" aceita pela Meta para ${v.nome} (${r.telefone}). Chega no WhatsApp em instantes.` });
      cliente.invalidateQueries({ queryKey: ["vendedores", tenant] });
    },
    onError: (e: any, { v }) => {
      setEnvio(null);
      setRecado({ erro: true, texto: `${v.nome}: ${e?.mensagem || "não consegui enviar agora."}` });
    },
  });

  /* Liberar e revogar direto na lista, sem abrir o vendedor.

     Sem diálogo de confirmação de propósito: o executivo libera o piloto
     inteiro de uma vez, e vinte confirmações seguidas viram clique no
     automático — que é pior que nenhuma. No lugar, "Desfazer" no aviso.
     Trocar de número continua no painel do vendedor. */
  const alternar = useMutation({
    mutationFn: ({ v, ligar }: { v: Vendedor; ligar: boolean }) =>
      api.call<{ ok: boolean; telefone?: string }>(
        `/api/vendedores/${encodeURIComponent(v.chave)}/acesso`,
        ligar
          ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }
          : {
              method: "DELETE", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ motivo: "revogado pela lista de vendedores" }),
            },
      ),
    onMutate: ({ v, ligar }) => {
      setAlternando((s) => new Set(s).add(v.chave));
      const antes = cliente.getQueryData<Resposta>(["vendedores", tenant]);
      if (antes) {
        cliente.setQueryData<Resposta>(["vendedores", tenant], {
          ...antes,
          vendedores: antes.vendedores.map((x) => (x.chave === v.chave ? { ...x, tem_whatsapp: ligar } : x)),
          resumo: { ...antes.resumo, com_whatsapp: antes.resumo.com_whatsapp + (ligar ? 1 : -1) },
        });
      }
      return { antes };
    },
    onError: (e: any, { v, ligar }, ctx) => {
      if (ctx?.antes) cliente.setQueryData(["vendedores", tenant], ctx.antes);
      setRecado({
        erro: true,
        texto: `${v.nome}: ${e?.mensagem || (ligar ? "não consegui liberar agora." : "não consegui revogar agora.")}`,
      });
    },
    onSuccess: (r, { v, ligar }) => {
      setRecado({
        texto: ligar
          ? `${v.nome} liberado${r?.telefone ? ` em ${r.telefone}` : ""}. A Sophia passa a responder.`
          : `${v.nome} revogado. A Sophia deixa de responder.`,
        desfazer: () => alternar.mutate({ v, ligar: !ligar }),
      });
    },
    onSettled: (_r, _e, { v }) => {
      setAlternando((s) => { const n = new Set(s); n.delete(v.chave); return n; });
      // a verdade vem do servidor: o otimista só evita a chave piscar
      cliente.invalidateQueries({ queryKey: ["vendedores", tenant] });
      cliente.invalidateQueries({ queryKey: ["vendedor", tenant, v.chave] });
    },
  });

  const lista = useMemo(() => {
    const todos = q.data?.vendedores ?? [];
    const termo = busca.trim().toLowerCase();
    return todos.filter((v) => {
      if (equipe && (v.unidade ?? "") !== equipe) return false;
      if (filtro === "parados" && !(v.ativo && !(v.liquido_atual ?? 0))) return false;
      if (filtro === "sem_whatsapp" && (v.tem_whatsapp || !v.ativo)) return false;
      if (filtro === "usaram" && !v.turnos) return false;
      // "têm e não usam" só faz sentido para quem TEM acesso: para quem não
      // tem, não usar é consequência, não comportamento.
      if (filtro === "nunca_usaram" && (!v.tem_whatsapp || v.turnos > 0)) return false;
      if (!termo) return true;
      return (
        v.nome.toLowerCase().includes(termo) ||
        v.chave.includes(termo) ||
        (v.unidade ?? "").toLowerCase().includes(termo) ||
        (v.unidade_chave ?? "").toLowerCase().includes(termo)
      );
    });
  }, [q.data, busca, filtro, equipe]);

  const r = q.data?.resumo;
  const p = q.data?.periodo;
  const janela = q.data?.janela_uso_dias ?? 15;
  const rotuloUnidade = q.data?.rotulo_unidade ?? "Unidade";

  return (
    <>
      <PageHeader
        eyebrow="Vendas"
        title="Vendedores"
        subtitle={
          p
            ? `${dia(p.de)} a ${dia(p.ate)} · comparado com ${dia(p.comparado_de)} a ${dia(p.comparado_ate)}`
            : "Resultado da equipe, uso da Sophia e acesso ao WhatsApp."
        }
      />

      {q.data?.stale && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {q.data.error === "sem_base"
            ? "A base desta empresa ainda não foi publicada — não há vendedores para mostrar."
            : "Não consegui ler a base agora. Tente de novo em alguns minutos."}
        </Alert>
      )}

      {/* O contador que pede ação imediata: alguém perguntou e a Sophia não
          pôde responder porque falta chave de API cadastrada. */}
      {!!r?.bloqueios_sem_chave && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{r.bloqueios_sem_chave} pergunta(s) ficaram sem resposta</strong> nos
          últimos {janela} dias porque falta cadastrar a chave de API desta empresa.
          Resolve em <a href="/modelo-ia">Modelo de IA</a>.
        </Alert>
      )}

      {r && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Numero rotulo="Vendedores" valor={num(r.total)} nota={`${r.ativos} ativos`} />
          <Numero rotulo="Venderam no período" valor={num(r.venderam)} />
          <Numero
            rotulo="Ativos sem venda" valor={num(r.parados)}
            cor={r.parados > 0 ? "warning.main" : undefined}
          />
          <Numero
            rotulo="Com Sophia" valor={num(r.com_whatsapp)}
            nota={r.total ? `${Math.round((r.com_whatsapp / r.total) * 100)}% da equipe` : undefined}
          />
          {/* Ter acesso não é usar. Na Jotuje esses dois números eram 115 e 0. */}
          <Numero
            rotulo={`Usaram (${janela}d)`} valor={num(r.usaram_sophia)}
            nota={r.com_whatsapp ? `de ${r.com_whatsapp} com acesso` : undefined}
            cor={r.com_whatsapp > 0 && r.usaram_sophia === 0 ? "warning.main" : undefined}
          />
          <Numero
            rotulo={`Custo Sophia (${janela}d)`} valor={brlExato(r.custo_brl)}
            nota={`${num(r.tokens)} tokens`}
          />
        </Stack>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }} alignItems="center">
        <TextField
          size="small" placeholder="Buscar por nome, código ou equipe"
          value={busca} onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        {/* Lista em vez de texto livre: o gerente não tem de adivinhar como a
            equipe foi escrita no ERP ("EQP TOP - GILVAN"). */}
        <TextField
          select size="small" label={rotuloUnidade} value={equipe}
          onChange={(e) => setEquipe(e.target.value)} sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {(q.data?.unidades ?? []).map((u) => (
            <MenuItem key={u.nome} value={u.nome}>
              {u.nome} ({u.vendedores})
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small" exclusive value={filtro}
          onChange={(_, v) => v && setFiltro(v)} sx={{ flexWrap: "wrap" }}
        >
          <ToggleButton value="todos">Todos</ToggleButton>
          <ToggleButton value="parados">Sem venda</ToggleButton>
          <ToggleButton value="sem_whatsapp">Sem Sophia</ToggleButton>
          <ToggleButton value="usaram">Usaram</ToggleButton>
          <ToggleButton value="nunca_usaram">Têm e não usam</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", whiteSpace: "nowrap" }}>
          {lista.length} de {r?.total ?? 0}
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vendedor</TableCell>
              {!isSm && <TableCell>{rotuloUnidade}</TableCell>}
              <TableCell align="right">Faturamento</TableCell>
              <TableCell align="right">vs. anterior</TableCell>
              {!isSm && <TableCell align="right">Pedidos</TableCell>}
              {!isSm && <TableCell align="right">Clientes</TableCell>}
              {!isSm && <TableCell align="right">Sophia ({janela}d)</TableCell>}
              <TableCell align="center">Acesso</TableCell>
              {podeGerenciar && templates.length > 0 && <TableCell align="center">Enviar</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {q.isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={isSm ? 4 : 8}><Skeleton /></TableCell>
                </TableRow>
              ))}

            {!q.isLoading && lista.length === 0 && (
              <TableRow>
                <TableCell colSpan={isSm ? 4 : 8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                    {filtro === "parados"
                      ? "Nenhum vendedor ativo ficou sem vender no período."
                      : filtro === "sem_whatsapp"
                        ? "Todos os vendedores ativos têm a Sophia liberada."
                        : filtro === "nunca_usaram"
                          ? "Todos os que têm acesso conversaram no período."
                          : "Nenhum vendedor encontrado."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {lista.map((v) => (
              <TableRow
                key={v.chave} hover sx={{ cursor: "pointer" }}
                onClick={() => setAberto(v)}
              >
                <TableCell>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{v.nome}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    {v.chave}
                    {!v.ativo && " · inativo no cadastro"}
                  </Typography>
                </TableCell>
                {!isSm && <TableCell sx={{ fontSize: 13 }}>{v.unidade || "—"}</TableCell>}
                <TableCell align="right" sx={{ fontSize: 13.5, fontVariantNumeric: "tabular-nums" }}>
                  {brl(v.liquido_atual)}
                </TableCell>
                <TableCell align="right">
                  <Delta valor={v.delta_pct} tinhaAntes={!!v.liquido_antes} />
                </TableCell>
                {!isSm && (
                  <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                    {v.pedidos_atual}
                  </TableCell>
                )}
                {!isSm && (
                  <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                    {v.clientes_atual}
                  </TableCell>
                )}
                {!isSm && (
                  <TableCell align="right" sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                    {v.turnos ? (
                      <Tooltip title={`${num(v.tokens)} tokens · ${brlExato(v.custo_brl)} · última em ${quando(v.ultimo_turno)}`}>
                        <span>{v.turnos} {v.turnos === 1 ? "conversa" : "conversas"}</span>
                      </Tooltip>
                    ) : (
                      <Typography component="span" sx={{ fontSize: 13, color: "text.disabled" }}>—</Typography>
                    )}
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  // a chave não pode abrir o painel do vendedor
                  onClick={podeGerenciar ? (e) => e.stopPropagation() : undefined}
                  sx={{ cursor: podeGerenciar ? "default" : undefined, whiteSpace: "nowrap" }}
                >
                  <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
                    <Tooltip
                      title={
                        v.tem_whatsapp
                          ? `Sophia liberada${v.telefone_vinculo ? ` · ${v.telefone_vinculo}` : ""}`
                          : v.tem_vinculo
                            ? `A Sophia não responde${v.telefone_vinculo ? ` · ${v.telefone_vinculo}` : ""}`
                            : v.telefone
                              ? `Telefone no ERP (${v.telefone}), mas sem vínculo — a Sophia não responde`
                              : "Sem telefone no cadastro e sem vínculo"
                      }
                    >
                      <WhatsAppIcon
                        fontSize="small"
                        sx={{ color: v.tem_whatsapp ? "success.main" : "action.disabled" }}
                      />
                    </Tooltip>
                    {podeGerenciar && (() => {
                      // Sem vínculo e sem telefone no ERP não há para onde
                      // liberar: o número se informa no painel do vendedor.
                      const semNumero = !v.tem_whatsapp && !v.tem_vinculo && !v.telefone;
                      return (
                        <Tooltip
                          title={
                            semNumero
                              ? "Sem telefone — abra o vendedor para informar o número"
                              : v.tem_whatsapp ? "Revogar a Sophia" : "Liberar a Sophia"
                          }
                        >
                          <span>
                            <Switch
                              size="small" color="success"
                              checked={v.tem_whatsapp}
                              disabled={semNumero || alternando.has(v.chave)}
                              onChange={(_e, ligar) => alternar.mutate({ v, ligar })}
                              inputProps={{ "aria-label": `Acesso de ${v.nome} à Sophia` }}
                            />
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </Stack>
                </TableCell>
                {podeGerenciar && templates.length > 0 && (
                  <TableCell
                    align="center" onClick={(e) => e.stopPropagation()}
                    sx={{ cursor: "default", whiteSpace: "nowrap" }}
                  >
                    {templates.map((t) => {
                      const Icone = ICONE_DO_TEMPLATE[t.id] ?? SendRoundedIcon;
                      const ultimo = v.envios?.[t.id];
                      const motivo =
                        t.status !== "APPROVED"
                          ? `${t.rotulo}: ${t.status_legivel}${t.motivo ? ` (${t.motivo})` : ""}`
                          : !v.tem_whatsapp
                            ? `Libere a ${assistente} para ${v.nome} antes de enviar "${t.rotulo}"`
                            : "";
                      return (
                        <Tooltip
                          key={t.id}
                          title={motivo || `Enviar "${t.rotulo}" para ${v.nome}${ultimo ? ` · último envio ${quando(ultimo)}` : ""}`}
                        >
                          <span>
                            <IconButton
                              size="small" disabled={!!motivo}
                              color={ultimo ? "success" : "default"}
                              onClick={() => abrirEnvio(v, t)}
                              aria-label={`Enviar ${t.rotulo} para ${v.nome}`}
                            >
                              <Icone fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    })}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <OQueMaisConsultam />

      <PainelDoVendedor
        vendedor={aberto}
        podeGerenciar={podeGerenciar}
        aoFechar={() => setAberto(null)}
      />

      <Dialog open={!!envio} onClose={() => !enviarTemplate.isPending && setEnvio(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar "{envio?.t.rotulo}"?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            Para <strong>{envio?.v.nome}</strong>
            {numerosAtendidos.length === 1 ? ` (${rotuloDoNumero(numerosAtendidos[0])})` : ""}. A mensagem sai agora pelo
            WhatsApp da {assistente} e não pode ser desfeita.
          </DialogContentText>
          {det.isLoading && <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />}
          {!det.isLoading && numerosAtendidos.length === 0 && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              Não encontrei número atendido para este vendedor.
            </Alert>
          )}
          {numerosAtendidos.length > 1 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Este vendedor tem {numerosAtendidos.length} números atendidos. Escolha para qual enviar:
              <RadioGroup value={numeroEscolhido} onChange={(_e, n) => setNumeroEscolhido(n)} sx={{ mt: 0.5 }}>
                {numerosAtendidos.map((n) => (
                  <FormControlLabel
                    key={n.telefone} value={n.telefone} control={<Radio size="small" />}
                    label={rotuloDoNumero(n)}
                  />
                ))}
              </RadioGroup>
            </Alert>
          )}
          {envio && envio.v.envios?.[envio.t.id] && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Já foi enviada para este vendedor em {quando(envio.v.envios[envio.t.id])}.
            </Alert>
          )}
          {envio?.t.corpo && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover", fontSize: 13.5, whiteSpace: "pre-wrap" }}>
              {envio.t.corpo.replace(/\{\{\s*1\s*\}\}/g, nomeDeSaudacao(envio.v.nome))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvio(null)} disabled={enviarTemplate.isPending}>Cancelar</Button>
          <Button
            variant="contained" startIcon={enviarTemplate.isPending ? <CircularProgress size={14} /> : <SendRoundedIcon />}
            disabled={enviarTemplate.isPending || !numeroDoEnvio}
            onClick={() => envio && numeroDoEnvio && enviarTemplate.mutate({ ...envio, telefone: numeroDoEnvio })}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!recado}
        autoHideDuration={recado?.erro ? 8000 : 6000}
        onClose={(_e, motivo) => { if (motivo !== "clickaway") setRecado(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={recado?.erro ? "error" : "success"} variant="filled" sx={{ alignItems: "center" }}
          onClose={() => setRecado(null)}
          action={recado?.desfazer ? (
            <Button
              color="inherit" size="small"
              onClick={() => { const d = recado.desfazer; setRecado(null); d?.(); }}
            >
              Desfazer
            </Button>
          ) : undefined}
        >
          {recado?.texto}
        </Alert>
      </Snackbar>
    </>
  );
}

/**
 * O que a equipe mais pergunta à Sophia.
 *
 * A série começa no dia em que o registro de ferramentas subiu: antes disso o
 * turno guardava `tool_calls_count`, um número sem nome — dava para saber que
 * três ferramentas rodaram, nunca quais nem sobre quem. Por isso o vazio aqui
 * diz "começou agora" em vez de fingir que ninguém consultou nada.
 */
function OQueMaisConsultam() {
  const api = useApi();
  const { tenant } = useTenant();
  const q = useQuery({
    queryKey: ["vendedores-consultas", tenant],
    queryFn: () => api.call<Ranking>("/api/vendedores/consultas"),
    enabled: !!tenant,
  });

  if (!q.data) return null;
  const { ferramentas, alvos, turnos_com_registro, turnos_na_janela, janela_dias } = q.data;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, p: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
        O que a equipe mais consulta
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 1.5 }}>
        Últimos {janela_dias} dias · {turnos_com_registro} de {turnos_na_janela} conversas
        com detalhe registrado
      </Typography>

      {turnos_com_registro === 0 ? (
        <Alert severity="info" sx={{ fontSize: 13 }}>
          Ainda não há histórico. Até agora o sistema só contava quantas consultas
          cada conversa fez, sem guardar quais — o registro detalhado começou
          agora e vai aparecer aqui conforme a equipe usar a Sophia.
        </Alert>
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.75 }}>
              Consultas
            </Typography>
            <Stack spacing={0.5}>
              {ferramentas.map((f) => (
                <Stack key={f.nome} direction="row" spacing={1}>
                  <Typography sx={{ fontSize: 13, flex: 1, fontFamily: "ui-monospace, monospace" }}>
                    {f.nome}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{f.vezes}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.75 }}>
              Sobre quem
            </Typography>
            {alvos.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                As consultas do período foram gerais, sem cliente ou produto específico.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {alvos.map((a, i) => (
                  <Stack key={i} direction="row" spacing={1}>
                    <Typography sx={{ fontSize: 13, flex: 1 }}>{a.valor}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{a.vezes}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ painel */

function PainelDoVendedor({
  vendedor, podeGerenciar, aoFechar,
}: { vendedor: Vendedor | null; podeGerenciar: boolean; aoFechar: () => void }) {
  const api = useApi();
  const { tenant } = useTenant();
  const cliente = useQueryClient();
  const [confirmar, setConfirmar] = useState<"liberar" | "revogar" | null>(null);
  const [telefone, setTelefone] = useState("");
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");

  const chave = vendedor?.chave;
  const d = useQuery({
    queryKey: ["vendedor", tenant, chave],
    queryFn: () => api.call<Detalhe>(`/api/vendedores/${encodeURIComponent(chave!)}`),
    enabled: !!chave && !!tenant,
  });

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ["vendedor", tenant, chave] });
    cliente.invalidateQueries({ queryKey: ["vendedores", tenant] });
  };

  const liberar = useMutation({
    mutationFn: () =>
      api.call<{
        ok: boolean; telefone: string; ja_existia: boolean;
        contexto_definido: boolean; movido_de?: string;
      }>(
        `/api/vendedores/${encodeURIComponent(chave!)}/acesso`,
        {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ telefone: telefone.trim() || undefined }),
        },
      ),
    onSuccess: (r) => {
      setErro(""); setConfirmar(null); setTelefone("");
      setAviso(
        r.movido_de
          // Trocar de número é mudar de vínculo: o antigo foi desligado, e
          // dizer só "atualizado" esconderia metade do que aconteceu.
          ? `Número alterado de ${r.movido_de} para ${r.telefone}. O antigo foi desligado.`
          // Com vínculo e sem atender (revogado, ou barrado pela trava),
          // liberar MUDOU alguma coisa — "confirmado" esconderia isso.
          : r.ja_existia && atende
            ? `Vínculo confirmado em ${r.telefone}.`
            : r.ja_existia
              ? `Sophia liberada para ${r.telefone}.`
            : `Sophia liberada para ${r.telefone}. ${r.contexto_definido ? "Contexto da empresa definido." : "O contexto já existia."}`,
      );
      recarregar();
    },
    onError: (e: any) => setErro(e?.mensagem || "Não consegui liberar agora."),
  });

  const revogar = useMutation({
    mutationFn: () =>
      api.call<{ ok: boolean }>(`/api/vendedores/${encodeURIComponent(chave!)}/acesso`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ motivo: "revogado pela plataforma" }),
      }),
    onSuccess: () => {
      setErro(""); setConfirmar(null);
      setAviso("Acesso revogado. A Sophia deixa de responder para este número.");
      recarregar();
    },
    onError: (e: any) => setErro(e?.mensagem || "Não consegui revogar agora."),
  });

  const v = d.data?.vinculo;
  // `atende` vem do backend; sem ele (resposta antiga), a regra de antes.
  const atende = !!v?.tem_vinculo && (v.atende ?? v.liberado !== false);
  const uso = d.data?.uso ?? {};
  const janela = d.data?.janela_dias ?? 15;
  const ocupado = liberar.isPending || revogar.isPending;

  // O número que vai valer, na mesma ordem que o backend resolve: o digitado,
  // senão o do vínculo que já funciona, senão o do ERP. Mostrar o do ERP quando
  // existe vínculo em outro número faria o gerente confirmar um e gravar outro.
  const numeroDoVinculo = v?.tem_vinculo ? v.telefone : "";
  const numeroEfetivo = telefone.trim() || numeroDoVinculo || d.data?.telefone_erp || vendedor?.telefone || "";
  const vaiTrocar = !!numeroDoVinculo && !!telefone.trim() && !mesmoNumero(telefone, numeroDoVinculo);

  return (
    <>
      <Drawer
        anchor="right" open={!!vendedor}
        onClose={() => { aoFechar(); setAviso(""); setErro(""); }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, p: 2.5 } }}
      >
        {vendedor && (
          <>
            <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, color: "text.secondary", letterSpacing: 0.6 }}>
                  {vendedor.unidade || "SEM EQUIPE"}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{vendedor.nome}</Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  código {vendedor.chave}
                  {!vendedor.ativo && " · inativo no cadastro"}
                </Typography>
              </Box>
              <Button size="small" onClick={aoFechar} sx={{ minWidth: 0 }}>
                <CloseRoundedIcon fontSize="small" />
              </Button>
            </Stack>

            {!!aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
            {!!erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro("")}>{erro}</Alert>}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
              <Mini rotulo="Faturamento" valor={brl(vendedor.liquido_atual)} />
              <Mini rotulo="Pedidos" valor={String(vendedor.pedidos_atual)} />
              <Mini rotulo="Clientes" valor={String(vendedor.clientes_atual)} />
              <Mini rotulo="Última venda" valor={dia(vendedor.ultima_venda)} />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
              Acesso à Sophia
            </Typography>

            {d.isLoading && <Skeleton variant="rounded" height={90} />}

            {d.data && (
              <>
                {/* Quem manda aqui é `atende`, não `ativo` nem `liberado`.

                    `ativo` é o vínculo, que pertence à integração: o sync das
                    04:30 lê o ERP e o regrava toda noite. Revogar deixou de
                    mexer nele justamente por isso, então ele continua `true`
                    depois de uma revogação. E `liberado` sozinho também não
                    basta: com a trava do cliente ligada, quem nunca foi
                    decidido (`null`) é barrado. `atende` vem do mesmo portão
                    que a conversa usa. */}
                {atende ? (
                  <Alert severity="success" icon={<WhatsAppIcon fontSize="small" />} sx={{ mb: 1.5 }}>
                    Atendido em <strong>{v.telefone}</strong>
                    {v.liberado === true && v.decidido_por
                      ? ` · liberado por ${quemLiberou(v.decidido_por)}`
                      : v.liberado_por ? ` · vínculo criado por ${quemLiberou(v.liberado_por)}` : ""}
                    {v.liberado === true && v.decidido_em ? ` em ${quando(v.decidido_em)}` : ""}
                  </Alert>
                ) : (
                  <Alert severity={v?.tem_vinculo ? "warning" : "info"} sx={{ mb: 1.5 }}>
                    {!v?.tem_vinculo
                      ? "Sem vínculo. A Sophia não responde para este vendedor."
                      : v.liberado === false
                        ? "Acesso revogado — a Sophia não responde para este vendedor."
                        : "Aguardando liberação — neste cliente a Sophia só responde a quem foi liberado aqui."}
                    {v?.tem_vinculo && v.liberado === false && v.decidido_por
                      ? ` Revogado por ${quemLiberou(v.decidido_por)}${v.decidido_em ? ` em ${quando(v.decidido_em)}` : ""}.`
                      : ""}
                    {v?.motivo ? ` Motivo: ${v.motivo}.` : ""}
                    {!vendedor.telefone && !v?.telefone && (
                      <> Não há telefone no cadastro do ERP, então informe um abaixo.</>
                    )}
                  </Alert>
                )}

                {/* Um RCA com mais de um número quase sempre é troca de
                    telefone que ninguém limpou. Seis RCAs da Jotujé estão
                    assim. Revogar fecha todos; a tela mostra quais são. */}
                {(v?.telefones?.length ?? 0) > 1 && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    Este vendedor tem <strong>{v?.telefones?.length} números</strong> no
                    cadastro:{" "}
                    {v?.telefones?.map((t) => (
                      `${t.telefone}${t.nome ? ` · ${t.nome}` : ""} (${t.liberado === true ? "liberado"
                        : t.liberado === false ? "revogado"
                          : t.atende ? "nunca decidido, atendido" : "nunca decidido, barrado"})`
                    )).join(", ")}
                    . Revogar fecha todos de uma vez.
                  </Alert>
                )}

                {podeGerenciar ? (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                    <TextField
                      size="small" label="Telefone (DDD + número)"
                      placeholder={numeroEfetivo || "85 99999-0000"}
                      value={telefone} onChange={(e) => setTelefone(e.target.value)}
                      helperText={
                        numeroDoVinculo
                          ? "Vazio mantém o número atual"
                          : "Vazio usa o telefone do ERP"
                      }
                      sx={{ minWidth: 220 }}
                    />
                    <Button
                      variant="contained" size="small" startIcon={<BoltRoundedIcon />}
                      disabled={ocupado} onClick={() => { setErro(""); setConfirmar("liberar"); }}
                    >
                      {atende ? "Atualizar acesso" : "Liberar Sophia"}
                    </Button>
                    {atende && (
                      <Button
                        variant="outlined" color="error" size="small" startIcon={<BlockRoundedIcon />}
                        disabled={ocupado} onClick={() => { setErro(""); setConfirmar("revogar"); }}
                      >
                        Revogar
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ mb: 2.5 }}>
                    Você pode ver o acesso, mas não alterá-lo. Liberar ou revogar exige
                    o perfil executivo ou de administração do cliente.
                  </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                  Consumo nos últimos {janela} dias
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Mini rotulo="Conversas" valor={num(uso.turnos)} />
                  <Mini rotulo="Com modelo" valor={num(uso.turnos_com_modelo)} />
                  <Mini rotulo="Tokens" valor={num((uso.tokens_entrada ?? 0) + (uso.tokens_saida ?? 0))} />
                  <Mini rotulo="Custo" valor={brlExato(uso.custo_brl)} />
                </Stack>
                {/* Turno sem token não é falha: é o caminho barato funcionando. */}
                {!!uso.turnos && (
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 2 }}>
                    Conversa sem token foi resolvida sem chamar o modelo — custa zero.
                    {uso.modelo ? ` Modelo em uso: ${uso.modelo}.` : ""}
                  </Typography>
                )}

                {!!uso.bloqueios_sem_chave && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {uso.bloqueios_sem_chave} pergunta(s) sem resposta por falta de chave de API.
                  </Alert>
                )}

                <Divider sx={{ mb: 2 }} />

                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                  Conversas ({d.data.conversas.length})
                </Typography>
                {d.data.conversas.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    {v?.telefone
                      ? `Nenhuma conversa nos últimos ${janela} dias.`
                      : "Sem vínculo, não há conversa para mostrar."}
                  </Typography>
                )}
                <Stack spacing={1.25}>
                  {d.data.conversas.map((c) => <Turno key={c.turn_id} c={c} />)}
                </Stack>
              </>
            )}
          </>
        )}
      </Drawer>

      {/* A confirmação nomeia a consequência: não é "tem certeza?", é o que
          vai acontecer com uma pessoa real do outro lado. */}
      <Dialog open={!!confirmar} onClose={() => !ocupado && setConfirmar(null)}>
        <DialogTitle>
          {confirmar === "revogar"
            ? "Revogar o acesso?"
            : vaiTrocar ? "Trocar o número?" : "Liberar a Sophia?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {confirmar === "liberar" ? (
              vaiTrocar ? (
                <>
                  <strong>{vendedor?.nome}</strong> passa a ser atendido em{" "}
                  <strong>{telefone.trim()}</strong> e deixa de ser atendido em{" "}
                  <strong>{numeroDoVinculo}</strong> — o número antigo é bloqueado.
                </>
              ) : (
                <>
                  <strong>{vendedor?.nome}</strong> passa a ser atendido pela Sophia no
                  WhatsApp em <strong>{numeroEfetivo || "—"}</strong>.
                  As perguntas dele consomem a chave de API desta empresa. Dá para
                  revogar depois, aqui mesmo.
                </>
              )
            ) : (
              <>
                <strong>{vendedor?.nome}</strong> deixa de ser atendido a partir de
                agora
                {(v?.telefones?.length ?? 0) > 1
                  ? `, nos ${v?.telefones?.length} números do cadastro dele`
                  : ""}
                . O histórico de conversas continua visível até expirar.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)} disabled={ocupado}>Cancelar</Button>
          <Button
            variant="contained" color={confirmar === "revogar" ? "error" : "primary"}
            disabled={ocupado}
            startIcon={ocupado ? <CircularProgress size={14} /> : undefined}
            onClick={() => (confirmar === "liberar" ? liberar.mutate() : revogar.mutate())}
          >
            {confirmar === "liberar" ? "Liberar" : "Revogar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Turno({ c }: { c: Conversa }) {
  const semModelo = !c.modelo && c.status !== "failed" && c.tratado_por !== "sem_chave_do_cliente";
  return (
    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{quando(c.quando)}</Typography>
        {c.status === "failed" && (
          <Chip size="small" color="error" label="falhou" sx={{ height: 18, fontSize: 10.5 }} />
        )}
        {c.tratado_por === "sem_chave_do_cliente" && (
          <Chip size="small" color="warning" label="sem chave" sx={{ height: 18, fontSize: 10.5 }} />
        )}
        {semModelo && (
          <Tooltip title={`Resolvido por ${c.tratado_por || "código"} — sem chamar o modelo`}>
            <Chip size="small" variant="outlined" label="sem custo" sx={{ height: 18, fontSize: 10.5 }} />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {c.modelo ? `${num(c.tokens_entrada + c.tokens_saida)} tok · ${brlExato(c.custo_brl)} · ` : ""}
          {Math.round(c.duracao_ms / 100) / 10}s
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 0.5 }}>{c.pergunta || "—"}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary", whiteSpace: "pre-wrap" }}>
        {c.resposta ? c.resposta.slice(0, 400) : c.erro || "(sem resposta)"}
        {c.resposta.length > 400 ? "…" : ""}
      </Typography>
      {!!c.ferramentas.length && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {c.ferramentas.map((f, i) => (
            <Chip
              key={i} size="small" variant="outlined"
              label={
                Object.values(f.alvo || {}).length
                  ? `${f.nome}: ${Object.values(f.alvo).join(", ")}`
                  : f.nome
              }
              sx={{ height: 19, fontSize: 10.5, fontFamily: "ui-monospace, monospace" }}
            />
          ))}
        </Stack>
      )}
    </Card>
  );
}

/* ----------------------------------------------------------------- pedaços */

function Numero({ rotulo, valor, nota, cor }: { rotulo: string; valor: string; nota?: string; cor?: string }) {
  return (
    <Card variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 2, minWidth: 150 }}>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{rotulo}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: cor }}>{valor}</Typography>
      {nota && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{nota}</Typography>}
    </Card>
  );
}

function Mini({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <Box sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: "action.hover", minWidth: 96 }}>
      <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>{rotulo}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{valor}</Typography>
    </Box>
  );
}

function Delta({ valor, tinhaAntes }: { valor: number | null; tinhaAntes: boolean }) {
  if (valor == null) {
    // Sem base de comparação: "novo" é informação, "0%" seria mentira.
    return (
      <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
        {tinhaAntes ? "—" : "novo"}
      </Typography>
    );
  }
  const sobe = valor >= 0;
  return (
    <Chip
      size="small"
      label={`${sobe ? "+" : ""}${valor.toFixed(1)}%`}
      sx={{
        height: 20, fontSize: 11.5, fontWeight: 600,
        color: sobe ? "success.dark" : "error.dark",
        bgcolor: sobe ? "success.light" : "error.light",
        opacity: 0.9,
      }}
    />
  );
}

export default Vendedores;
